import { BiUpvote } from 'react-icons/bi';
import styled from 'styled-components';
import { useRef, useState, useEffect } from 'react';
import { imageHoverAnimationInFeed } from '../../../styleHelpers';
import { SharedImage } from '../../../types';
import { SmoothlyLoadingImage } from '../../common/SmoothlyLoadingImage';
import { PLACEHOLDER_IMAGE_PATH } from '../GeneratedImage';
import useHandleMarkingImageAsSeen from '../../../hooks/feed/useHandleMarkingImageAsSeen';
import AlreadySeenImagesStorage from '../../../localStorage/alreadySeenImagesStorage';
import { FaImages } from 'react-icons/fa6';
import { fadeInWithBounce } from '../animations';
import { getUrlToNavigateInImageGrid } from './SharedImagesGrid';

interface ImageTemplateNewProps {
  image: SharedImage;
  index: number;
  handleClickImageOpen: (image: SharedImage, index: number) => void;
  shouldMarkAsSeen?: boolean;
}

const ImageInGrid = ({
  image,
  index,
  handleClickImageOpen,
  shouldMarkAsSeen,
}: ImageTemplateNewProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const containerRef = useRef<HTMLAnchorElement>(null);

  // Helper to select low resolution image if available, else fallback to normal imageUrl
  const getPreferredImageUrl = (fakeImage?: { lowResImageUrl?: string; imageUrl?: string }) =>
    fakeImage?.lowResImageUrl || fakeImage?.imageUrl;

  // Use lowResImageUrl if available
  const currentImageUrl =
    image.album && image.album.length > 1
      ? getPreferredImageUrl(image.album[currentImageIndex])
      : getPreferredImageUrl(image?.image);

  const TIME_PER_IMAGE_IN_MS = 3200 + Math.random() * 8500;
  const isAlbum = image.album && image.album.length > 1;
  let amountOfCycles = 0;
  const MAX_CYCLES = 3;
  useEffect(() => {
    if (!isAlbum) return;

    let intervalId: NodeJS.Timeout;
    const initialDelay = Math.random() * 9500;

    const timeoutId = setTimeout(() => {
      intervalId = setInterval(() => {
        if (amountOfCycles >= MAX_CYCLES) {
          clearInterval(intervalId);
          return;
        }
        setCurrentImageIndex((current) =>
          current === image.album!.length - 1 ? 0 : current + 1,
        );
        amountOfCycles++;
      }, TIME_PER_IMAGE_IN_MS);
    }, initialDelay);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [isAlbum, image.album]);

  useHandleMarkingImageAsSeen({
    containerRef,
    shouldMarkAsSeen,
    imageId: image.id,
  });

  const handleImageError = () => {
    const target = containerRef.current;
    if (!target) return;
    target.style.display = 'none'; // hide the image
  };

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    AlreadySeenImagesStorage.addImageId(image.id);
    e.preventDefault();
    handleClickImageOpen(image, index);
  };

  const urlToNavigate = getUrlToNavigateInImageGrid(image);
  const channelName = image.channel?.name;
  const altTextToRender = channelName
    ? `Image from a user in channel ${channelName}`
    : 'A feed image';

  if (!currentImageUrl) return null;

  return (
    <Container 
      onClick={handleClick} 
      ref={containerRef}
      href={urlToNavigate}
      aria-label={`View ${altTextToRender}`}
    >
      <ImageWrapper>
        <AnimatedImage
          key={`${image.id}-${currentImageIndex}`}
          src={currentImageUrl || PLACEHOLDER_IMAGE_PATH}
          alt={altTextToRender}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          id={image.id}
          loaded={loaded}
          onError={handleImageError}
        />
      </ImageWrapper>
      <AmountOfUpvotesBadge>
        <BiUpvote size={12} />
        {image.amountOfRemixes}
      </AmountOfUpvotesBadge>

      {isAlbum && (
        <IsAlbumBadge>
          <FaImages />
        </IsAlbumBadge>
      )}
    </Container>
  );
};

export default ImageInGrid;

const Container = styled.a`
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 260px;
  max-height: 340px;
  text-decoration: none;
  color: inherit;
  display: block;
`;

const AmountOfUpvotesBadge = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  position: absolute;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.2);
  color: white;
  padding: 8px;
  font-size: 12px;
  border-radius: 0 10px 0 0;
  cursor: default;
`;

const IsAlbumBadge = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  position: absolute;
  bottom: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.2);
  color: white;
  padding: 10px 10px;
  font-size: 16px;
  border-radius: 10px 0 0 0;
  cursor: default;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const AnimatedImage = styled(SmoothlyLoadingImage)`
  object-fit: cover;
  width: 100%;
  height: 100%;
  ${imageHoverAnimationInFeed}

  animation: ${fadeInWithBounce} 0.3s ease-in-out;
`;
